import { NavigationEnd, Router } from '@angular/router';
import { Component, Inject, OnInit, Renderer2, OnDestroy } from '@angular/core';
import { filter, tap, take, delay, catchError } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { UpdateCookieConsent, UpdateGoogleAnalyticsClientId, UpdateUserState } from './core/ngrx/action/user.action';
import { getUserIdSelector } from './core/ngrx/state/user.state';
import { CookieConsentService, DatadogService } from '@dn/libs/ui/dn-common';
import { APP_CONFIG } from './core/tokens/app-config.token';
import { Environment } from '../environments/environment.interface';
import { ApplicationState } from './reducers';
import { CookieService } from 'ngx-cookie-service';
import { ConfigVersionService } from './core/services/config-version/config-version.service';
import { UpdateConfigVersionState } from './core/ngrx/action/config-version.action';
import { DOCUMENT } from '@angular/common';
import { createDatadogConfig } from './core/constants/datadog';
import { FaviconService } from './core/services/favicon.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  isGTMInjected = false;
  baseUrl: string;
  subscriptionsArray: Subscription[] = [];
  showBanner = false;
  constructor(
    private readonly router: Router,
    private readonly analyticsService: AnalyticsService,
    private readonly store: Store<ApplicationState>,
    private readonly cookieConsentService: CookieConsentService,
    private readonly configVersionService: ConfigVersionService,
    private readonly cookieService: CookieService,
    private readonly renderer2: Renderer2,
    private readonly datadogService: DatadogService,
    private readonly faviconService: FaviconService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(APP_CONFIG) private readonly appConfig: Environment,
  ) {
    this.baseUrl = this.appConfig.website_url;
  }

  ngOnInit(): void {
    const datadogConfig = createDatadogConfig(this.appConfig);
    this.datadogService.initService(datadogConfig);
    this.faviconService.initFavicon();
    this.injectGoogleOptimizeScript();
    this.checkCookieConsentAndGTM();
    this.checkConfigVersion();
  }

  checkCookieConsentAndGTM() {
    this.cookieConsentService.cookieConsentRequired
      .pipe(
        take(1),
        delay(3000),
        tap((isGDPR) => {
          if (!isGDPR) {
            this.activateGoogleTagManager();
          } else {
            this.showBanner = true;
          }
          const { country } = this.cookieConsentService;
          const payload = {
            country,
            isGDPR,
          };
          this.store.dispatch(UpdateUserState({ payload }));
        }),
      )
      .subscribe();
  }

  checkConfigVersion() {
    this.configVersionService
      .checkConfigVersion()
      .pipe(
        tap((configVersionRes) => {
          this.store.dispatch(UpdateConfigVersionState({ payload: configVersionRes }));
        }),
        catchError((err) => of('Error in configVersionService::', err)),
      )
      .subscribe();
  }

  activateGoogleTagManager() {
    this.analyticsService.initiateGoogleTagManager();
    this.setGTMEventForRoutes();
    this.isGTMInjected = true;
    const gaClientId = this.getCookieInInterval(this.appConfig.GTM_COOKIE_NAME);
    this.store.dispatch(UpdateGoogleAnalyticsClientId({ payload: { ga: this.analyticsService.fixGAClientId(gaClientId) } }));
    const cookieConsent = this.getCookieInInterval(this.appConfig.COOKIE_CONSENT_COOKIE_NAME);
    this.store.dispatch(UpdateCookieConsent({ payload: { cookieConsent: cookieConsent === 'true' } }));
  }

  setGTMEventForRoutes() {
    this.sendGTMPageToGA(); // For first landing on page

    // GTM page to GA Routing Events
    const routerEventsSub = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap(() => {
          this.sendGTMPageToGA();
        }),
      )
      .subscribe();
    this.subscriptionsArray.push(routerEventsSub);
  }

  sendGTMPageToGA() {
    this.store
      .select(getUserIdSelector)
      .pipe(take(1))
      .subscribe((userId) => {
        this.analyticsService.sendGtmPageEvent(userId);
      });
  }

  onCookieConsentClick() {
    if (!this.isGTMInjected) {
      this.activateGoogleTagManager();
    }
    this.showBanner = false;
  }

  getCookieInInterval(cookieName: string, tries = 0) {
    try {
      if (tries >= this.appConfig.GA_COOKIE_FETCH_RETRIES) {
        return;
      }
      const cookieValue = this.cookieService.get(cookieName);
      if (!cookieValue) {
        setTimeout(() => {
          this.getCookieInInterval(cookieName, tries++);
        }, this.appConfig.GA_COOKIE_FETCH_INTERVAL);
      } else {
        return cookieValue;
      }
    } catch (e) {}
  }

  injectGoogleOptimizeScript() {
    const script = this.renderer2.createElement('script');
    script.type = 'text/javascript';
    script.src = this.appConfig.GOOGLE_OPTIMIZE_SCRIPT;
    this.renderer2.appendChild(this.document.body, script);
  }

  ngOnDestroy() {
    this.subscriptionsArray.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
